import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserCircle, FloppyDisk, Lock, Buildings, Phone, IdentificationCard, EnvelopeSimple, ShieldCheck, Eye, EyeSlash, CheckCircle, WhatsappLogo, Headset, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Spinner from "@/components/Spinner";
import { authApi, configApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BankConfigPage from "@/pages/BankConfigPage";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "datos";
  const [tab, setTab] = useState(initialTab);
  const [profile, setProfile] = useState({
    nombre_empresa: "",
    nombre_responsable: "",
    telefono: "",
    ruc: "",
    titular_cuenta: "",
  });
  const [initialProfile, setInitialProfile] = useState(profile);
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [savingPwd, setSavingPwd] = useState(false);
  const [support, setSupport] = useState({ support_whatsapp: "", support_email: "" });

  useEffect(() => {
    configApi.public().then(setSupport).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const seed = {
      nombre_empresa: user.nombre_empresa || "",
      nombre_responsable: user.nombre_responsable || "",
      telefono: user.telefono || "",
      ruc: user.ruc || "",
      titular_cuenta: user.titular_cuenta || "",
    };
    setProfile(seed);
    setInitialProfile(seed);
  }, [user]);

  const isDirty = JSON.stringify(profile) !== JSON.stringify(initialProfile);

  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const setPwdField = (k) => (e) => setPwd((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.nombre_empresa.trim() || !profile.nombre_responsable.trim()) {
      return toast.warning("Los campos negocio y responsable son obligatorios");
    }
    setSavingProfile(true);
    try {
      await authApi.updateMe(profile);
      await refresh();
      setInitialProfile(profile);
      toast.success("Perfil actualizado correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar el perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) return toast.warning("La nueva contraseña debe tener al menos 6 caracteres");
    if (pwd.next !== pwd.confirm) return toast.warning("La confirmación no coincide");
    if (pwd.current === pwd.next) return toast.warning("La nueva debe ser distinta a la actual");
    setSavingPwd(true);
    try {
      await authApi.changePassword(pwd.current, pwd.next);
      toast.success("Contraseña actualizada");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar la contraseña");
    } finally {
      setSavingPwd(false);
    }
  };

  if (!user) return <div className="text-muted-foreground">Cargando…</div>;

  const trialActive = !!user.trial_until;

  return (
    <div className="max-w-4xl space-y-6" data-testid="profile-page">
      {/* Header identity card */}
      <div className="bg-card border border-border rounded-md p-6" data-testid="profile-header">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 grid place-items-center text-primary shrink-0">
            <UserCircle size={30} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline">Mi perfil</div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight mt-1 truncate">
              {user.nombre_empresa}
            </h2>
            <div className="text-sm text-muted-foreground mt-1 truncate">
              {user.email} · {user.nombre_responsable || "—"}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="outline" className="rounded-full">Plan {user.plan}</Badge>
              <Badge variant="outline" className="rounded-full border-success/40 text-success">
                <CheckCircle size={12} weight="bold" className="mr-1" /> {user.status}
              </Badge>
              {trialActive && (
                <Badge variant="outline" className="rounded-full border-primary/40 text-primary" data-testid="profile-trial-badge">
                  Prueba hasta {user.trial_until}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v);
          setSearchParams(v === "datos" ? {} : { tab: v }, { replace: true });
        }}
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full" data-testid="profile-tabs">
          <TabsTrigger value="datos" data-testid="tab-datos">
            <UserCircle size={14} weight="duotone" className="mr-1.5" /> Datos
          </TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password">
            <ShieldCheck size={14} weight="duotone" className="mr-1.5" /> Contraseña
          </TabsTrigger>
          <TabsTrigger value="recaudo" data-testid="tab-recaudo">
            <Bank size={14} weight="duotone" className="mr-1.5" /> Recaudo Bancario
          </TabsTrigger>
          <TabsTrigger value="soporte" data-testid="tab-soporte">
            <Headset size={14} weight="duotone" className="mr-1.5" /> Soporte
          </TabsTrigger>
        </TabsList>

        <TabsContent value="datos" className="mt-6">
      {/* Editable profile info */}
      <form onSubmit={saveProfile} className="bg-card border border-border rounded-md p-6 space-y-5" data-testid="profile-info-form">
        <div>
          <h3 className="font-heading font-semibold text-lg tracking-tight">Datos del negocio</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Actualiza tu información. El correo electrónico es tu identidad y no se puede modificar.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="p-empresa">Nombre del negocio <span className="text-destructive">*</span></Label>
            <div className="relative">
              <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="p-empresa" className="pl-9" value={profile.nombre_empresa} onChange={set("nombre_empresa")} required data-testid="input-nombre-empresa" />
            </div>
          </div>
          <div>
            <Label htmlFor="p-responsable">Responsable <span className="text-destructive">*</span></Label>
            <div className="relative">
              <UserCircle size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="p-responsable" className="pl-9" value={profile.nombre_responsable} onChange={set("nombre_responsable")} required data-testid="input-nombre-responsable" />
            </div>
          </div>

          <div>
            <Label htmlFor="p-tel">Teléfono / WhatsApp</Label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="p-tel" className="pl-9" value={profile.telefono} onChange={set("telefono")} minLength={7} data-testid="input-telefono" />
            </div>
          </div>
          <div>
            <Label htmlFor="p-ruc">RUC / Cédula</Label>
            <div className="relative">
              <IdentificationCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="p-ruc" className="pl-9" value={profile.ruc} onChange={set("ruc")} data-testid="input-ruc" />
            </div>
          </div>

          <div>
            <Label htmlFor="p-titular">Titular de la cuenta bancaria</Label>
            <Input id="p-titular" value={profile.titular_cuenta} onChange={set("titular_cuenta")} placeholder="Como aparece en tu banco" data-testid="input-titular-cuenta" />
            <p className="text-[11px] text-muted-foreground mt-1">Se usa en las cotizaciones y mensajes de cobro.</p>
          </div>
          <div>
            <Label>Correo electrónico</Label>
            <div className="relative">
              <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={user.email} disabled className="pl-9 bg-muted/40" data-testid="profile-email-readonly" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Contáctanos si necesitas cambiarlo.</p>
          </div>
        </div>

        <div className="pt-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {isDirty ? "Tienes cambios sin guardar." : "Todos los datos están al día."}
          </span>
          <Button type="submit" disabled={!isDirty || savingProfile} data-testid="save-profile-btn">
            {savingProfile ? <Spinner size="s" className="mr-2" /> : <FloppyDisk size={16} weight="duotone" className="mr-2" />}
            {savingProfile ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </form>
        </TabsContent>

        <TabsContent value="password" className="mt-6">
      {/* Password change */}
      <form onSubmit={savePassword} className="bg-card border border-border rounded-md p-6 space-y-5" data-testid="profile-password-form">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/10 grid place-items-center text-primary">
            <ShieldCheck size={20} weight="duotone" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg tracking-tight">Cambiar contraseña</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Se te pedirá la contraseña actual como verificación de seguridad.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: "current", label: "Contraseña actual", testid: "pwd-current" },
            { key: "next", label: "Nueva contraseña", testid: "pwd-next" },
            { key: "confirm", label: "Confirmar nueva", testid: "pwd-confirm" },
          ].map(({ key, label, testid }) => (
            <div key={key}>
              <Label htmlFor={`pwd-${key}`}>{label} <span className="text-destructive">*</span></Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id={`pwd-${key}`}
                  type={showPwd[key] ? "text" : "password"}
                  value={pwd[key]}
                  onChange={setPwdField(key)}
                  minLength={6}
                  required
                  autoComplete={key === "current" ? "current-password" : "new-password"}
                  data-testid={testid}
                  className="pl-9 pr-10"
                />
                <button
                  type="button"
                  aria-label={showPwd[key] ? "Ocultar" : "Mostrar"}
                  onClick={() => setShowPwd((s) => ({ ...s, [key]: !s[key] }))}
                  data-testid={`${testid}-toggle`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  {showPwd[key] ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-border flex justify-end">
          <Button
            type="submit"
            disabled={savingPwd || !pwd.current || !pwd.next || !pwd.confirm}
            data-testid="save-password-btn"
          >
            {savingPwd ? <Spinner size="s" className="mr-2" /> : <ShieldCheck size={16} weight="duotone" className="mr-2" />}
            {savingPwd ? "Cambiando…" : "Cambiar contraseña"}
          </Button>
        </div>
      </form>
        </TabsContent>

        <TabsContent value="recaudo" className="mt-6">
          <BankConfigPage />
        </TabsContent>

        <TabsContent value="soporte" className="mt-6">
      {/* Soporte */}
      <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="profile-support-section">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/10 grid place-items-center text-primary">
            <Headset size={20} weight="duotone" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg tracking-tight">Soporte</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              ¿Necesitas ayuda con tu cuenta, facturación o el uso de la plataforma? Escríbenos por WhatsApp.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center bg-[#25D366]/5 border border-[#25D366]/20 rounded-md p-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Número de soporte Recobro
            </div>
            <div className="mt-1 font-heading text-xl font-semibold tracking-tight flex items-center gap-2" data-testid="support-phone">
              <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" />
              {support.support_whatsapp || "0999725015"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Atención lunes a viernes · 9:00 a 18:00 (Ecuador)
            </div>
          </div>
          <a
            href={`https://wa.me/${(support.support_whatsapp || "0999725015").replace(/\D/g, "")}?text=${encodeURIComponent("Hola Recobro, necesito ayuda con mi cuenta.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-whatsapp-btn"
          >
            <Button className="bg-[#25D366] hover:bg-[#25D366]/90 text-white w-full md:w-auto">
              <WhatsappLogo size={16} weight="fill" className="mr-2" />
              Escribir por WhatsApp
            </Button>
          </a>
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
